import { DEFAULT_ORTHOPULSE_RECOMMENDATION_TIPTAP, EMPTY_TIPTAP_DOC } from '../templates';
import type { PricedProduct } from '../../../../../services/practiceProductPricing';
import {
    DX_REPORT_SECTION_IDS,
    type DxReportOrthopulseStatus,
    type DxReportPayload,
    type DxReportSection,
    type DxReportSectionId,
    type TiptapDoc
} from '../types';
import { feeLineFromProduct } from './feeVersion';

export const ORTHOPULSE_PRODUCT_NAME = 'OrthoPulse';

export interface SectionMeta {
    id: DxReportSectionId;
    defaultTitle: string;
    navLabel: string;
    placeholder?: string;
    defaultDoc: TiptapDoc;
    group: 'overview' | 'clinical' | 'recommendation';
    required: boolean;
}

export const SECTION_META: Record<DxReportSectionId, SectionMeta> = {
    'treatment-goals': {
        id: 'treatment-goals',
        defaultTitle: 'Treatment goals',
        navLabel: 'Treatment goals',
        placeholder: 'Document the treatment goals for this patient.',
        defaultDoc: EMPTY_TIPTAP_DOC,
        group: 'overview',
        required: false
    },
    findings: {
        id: 'findings',
        defaultTitle: 'Clinical findings',
        navLabel: 'Findings',
        placeholder:
            'Diagnostic findings (class II/III, crowding, missing teeth, etc.). Drop in radiograph or scan images to illustrate.',
        defaultDoc: EMPTY_TIPTAP_DOC,
        group: 'clinical',
        required: true
    },
    comments: {
        id: 'comments',
        defaultTitle: 'Comments',
        navLabel: 'Comments',
        placeholder: 'Notes on bite, condyle position, airway, periodontal status.',
        defaultDoc: EMPTY_TIPTAP_DOC,
        group: 'clinical',
        required: true
    },
    risks: {
        id: 'risks',
        defaultTitle: 'Risks & concerns',
        navLabel: 'Risks',
        placeholder: 'Adult-specific or case-specific risks (black triangles, bone quality, etc.).',
        defaultDoc: EMPTY_TIPTAP_DOC,
        group: 'clinical',
        required: true
    },
    'special-consent': {
        id: 'special-consent',
        defaultTitle: 'Special consent',
        navLabel: 'Consent',
        defaultDoc: EMPTY_TIPTAP_DOC,
        group: 'recommendation',
        required: true
    },
    'orthopulse-recommendation': {
        id: 'orthopulse-recommendation',
        defaultTitle: 'Orthopulse recommendation',
        navLabel: 'Orthopulse',
        placeholder:
            'One or two sentences on whether Orthopulse is indicated for this case, and why.',
        defaultDoc: DEFAULT_ORTHOPULSE_RECOMMENDATION_TIPTAP,
        group: 'recommendation',
        required: true
    },
    'estimated-treatment-time': {
        id: 'estimated-treatment-time',
        defaultTitle: 'Estimated treatment time',
        navLabel: 'Est. Tx time',
        placeholder: 'e.g. 12–18 months. Source pulled from Monday - you can override if needed.',
        defaultDoc: EMPTY_TIPTAP_DOC,
        group: 'recommendation',
        required: true
    }
};

export const REQUIRED_SECTION_IDS: DxReportSectionId[] = Object.values(SECTION_META)
    .filter((m) => m.required)
    .map((m) => m.id);

export interface CreateInitialPayloadInput {
    patientName: string;
    dateOfBirth?: string | null;
    doctorName?: string;
}

function computeAge(dobIso: string | null | undefined): number | null {
    if (!dobIso) return null;
    const dob = new Date(dobIso);
    if (Number.isNaN(dob.getTime())) return null;
    const now = new Date();
    let years = now.getFullYear() - dob.getFullYear();
    const m = now.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) years -= 1;
    return years >= 0 ? years : null;
}

export function createInitialPayload(input: CreateInitialPayloadInput): DxReportPayload {
    const today = new Date().toISOString().split('T')[0];
    const sections: DxReportSection[] = DX_REPORT_SECTION_IDS.map((id) => ({
        id,
        title: SECTION_META[id].defaultTitle,
        tiptapJson: SECTION_META[id].defaultDoc
    }));
    return {
        schemaVersion: 1,
        patientInfo: {
            patientName: input.patientName,
            dateOfBirth: input.dateOfBirth ?? null,
            age: computeAge(input.dateOfBirth ?? null),
            doctorName: input.doctorName ?? 'ODO ortho team',
            reportDate: today
        },
        sections,
        fees: {
            caseProduct: null,
            addons: [],
            recommendedPatientInvestmentCents: 0,
            currency: 'USD'
        },
        review: { state: 'editing', reviewerNote: null, history: [] },
        orthopulseStatus: 'recommended'
    };
}

// Syncs fees.addons with orthopulseStatus (mandatory/recommended adds the
// line, not_needed removes it); untouched if orthopulseProduct isn't loaded.
export function setOrthopulseStatus(
    payload: DxReportPayload,
    status: DxReportOrthopulseStatus,
    orthopulseProduct: PricedProduct | null
): DxReportPayload {
    const existingIndex = payload.fees.addons.findIndex(
        (a) => a.productName === ORTHOPULSE_PRODUCT_NAME
    );
    const hasAddon = existingIndex >= 0;
    let addons = payload.fees.addons;

    if (status === 'not_needed') {
        if (hasAddon) {
            addons = payload.fees.addons.filter((_, i) => i !== existingIndex);
        }
    } else if (!hasAddon && orthopulseProduct) {
        addons = [...payload.fees.addons, feeLineFromProduct(orthopulseProduct)];
    }

    return {
        ...payload,
        orthopulseStatus: status,
        fees: { ...payload.fees, addons }
    };
}

export interface SectionValidity {
    valid: boolean;
    reason: string | null;
}

const isTiptapEmpty = (doc: TiptapDoc): boolean => {
    if (!doc?.content || doc.content.length === 0) return true;
    const stack: unknown[] = [...doc.content];
    while (stack.length > 0) {
        const node = stack.pop() as { type?: string; text?: string; content?: unknown[] };
        if (!node) continue;
        if (typeof node.text === 'string' && node.text.trim().length > 0) return false;
        if (node.type === 'image') return false;
        if (node.type === 'horizontalRule') return false;
        if (Array.isArray(node.content)) stack.push(...node.content);
    }
    return true;
};

export function validatePayload(
    payload: DxReportPayload
): Record<DxReportSectionId | 'patient-info' | 'addons' | 'fees', SectionValidity> {
    const out = {} as Record<
        DxReportSectionId | 'patient-info' | 'addons' | 'fees',
        SectionValidity
    >;

    const pi = payload.patientInfo;
    if (!pi.patientName.trim()) {
        out['patient-info'] = { valid: false, reason: 'Patient name is required.' };
    } else if (!pi.doctorName.trim()) {
        out['patient-info'] = { valid: false, reason: 'Doctor name is required.' };
    } else if (!pi.reportDate) {
        out['patient-info'] = { valid: false, reason: 'Report date is required.' };
    } else {
        out['patient-info'] = { valid: true, reason: null };
    }

    for (const section of payload.sections) {
        if (isTiptapEmpty(section.tiptapJson)) {
            const meta = SECTION_META[section.id];
            out[section.id] = {
                valid: false,
                reason: meta?.required
                    ? `${meta.defaultTitle} is required.`
                    : 'Section is empty - add some content or skip.'
            };
        } else {
            out[section.id] = { valid: true, reason: null };
        }
    }

    out['addons'] = { valid: true, reason: null };

    if (!payload.fees.caseProduct) {
        out['fees'] = { valid: false, reason: 'Case product must be selected.' };
    } else if (payload.fees.recommendedPatientInvestmentCents <= 0) {
        out['fees'] = { valid: false, reason: 'Recommended patient investment must be set.' };
    } else {
        out['fees'] = { valid: true, reason: null };
    }

    return out;
}

export function isPayloadSubmittable(payload: DxReportPayload): boolean {
    const v = validatePayload(payload);
    if (!v['patient-info'].valid) return false;
    if (!v['fees'].valid) return false;
    for (const id of REQUIRED_SECTION_IDS) {
        if (!v[id]?.valid) return false;
    }
    return true;
}

export function setSectionTiptap(
    payload: DxReportPayload,
    id: DxReportSectionId,
    tiptapJson: TiptapDoc
): DxReportPayload {
    return {
        ...payload,
        sections: payload.sections.map((s) => (s.id === id ? { ...s, tiptapJson } : s))
    };
}

export { tiptapDocFromString } from '@odo/shared-ui';

export function stringFromTiptapDoc(doc: TiptapDoc): string {
    if (!doc?.content) return '';
    const out: string[] = [];
    for (const block of doc.content) {
        const node = block as { type?: string; content?: unknown[] };
        if (node.type !== 'paragraph') continue;
        const parts: string[] = [];
        for (const inline of node.content ?? []) {
            const inlineNode = inline as { type?: string; text?: string };
            if (inlineNode.type === 'text' && typeof inlineNode.text === 'string') {
                parts.push(inlineNode.text);
            } else if (inlineNode.type === 'hardBreak') {
                parts.push('\n');
            }
        }
        out.push(parts.join(''));
    }
    return out.join('\n\n');
}
